import { useState, useEffect } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { UserCircle, X, Plus, Star, Warning } from "@phosphor-icons/react";

/**
 * Admin widget to manage the in-house crew on a job or emergency_job (one or many operatives).
 * The first operative is the lead. Every change posts the full list to /{job|emergency_job}/{id}/assign-operative.
 * - entityType: "job" | "emergency_job"
 * - currentOperativeIds / currentOperativeNames: arrays from the record (legacy single fields accepted via fallback)
 * - onChange: callback invoked after a successful change (caller refetches)
 */
export default function InHouseOperativeAssigner({
  entityType, entityId, currentOperativeIds, currentOperativeNames, currentOperativeId, currentOperativeName, onChange, trade,
}) {
  const ids = (currentOperativeIds && currentOperativeIds.length) ? currentOperativeIds : (currentOperativeId ? [currentOperativeId] : []);
  const names = (currentOperativeNames && currentOperativeNames.length) ? currentOperativeNames : (currentOperativeName ? [currentOperativeName] : []);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [competency, setCompetency] = useState({});
  const [lastWarnings, setLastWarnings] = useState([]);

  // Competency matrix for the job's trade — warn-only badges next to each operative (never blocks assignment)
  useEffect(() => {
    let cancelled = false;
    API.get("/hs/competency", { params: trade ? { trade } : {} })
      .then(({ data }) => { if (!cancelled) setCompetency(Object.fromEntries((data.operatives || []).map((o) => [o.id, o]))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [trade]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.get("/in-house/team")
      .then(({ data }) => { if (!cancelled) setTeam(data.team || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async (nextIds, msg) => {
    setSaving(true);
    try {
      const { data } = await API.post(`/${entityType === "emergency_job" ? "emergency_job" : "job"}/${entityId}/assign-operative`, { operative_ids: nextIds });
      toast.success(msg);
      const warns = data?.hs_warnings || [];
      setLastWarnings(warns);
      warns.slice(0, 3).forEach((w) => toast.warning(`${w.operative_name}: ${w.text}`, { description: "Competency warning — assignment still saved", duration: 8000 }));
      setAdding(false);
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update assignment");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-xs text-muted-foreground italic">Loading team…</p>;
  if (!team.length && !ids.length) {
    return <p className="text-xs text-muted-foreground italic">No in-house team yet. Add operatives under Settings → In-House Team.</p>;
  }

  const available = team.filter(u => !ids.includes(u._id));
  const nameFor = (id, i) => names[i] || team.find(u => u._id === id)?.name || "Operative";
  const warnFor = (id) => competency[id]?.warnings || [];
  const warnTone = (ws) => (ws.some((w) => w.kind === "expired") ? "text-red-600" : ws.some((w) => w.kind === "missing") ? "text-amber-600" : "text-amber-500");
  const currentWarnings = ids.flatMap((id) => warnFor(id).map((w) => ({ ...w, operative_name: nameFor(id, ids.indexOf(id)) })));

  return (
    <div className="space-y-2" data-testid="in-house-crew">
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="in-house-operative-current">
          {ids.map((id, i) => (
            <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 pl-2 pr-1 py-0.5 text-xs" data-testid={`in-house-crew-${id}`}>
              {i === 0 ? <Star size={11} weight="fill" className="text-amber-600" title="Lead" /> : <UserCircle size={12} className="text-amber-600" />}
              <span className="font-medium">{nameFor(id, i)}</span>
              <button onClick={() => save(ids.filter(x => x !== id), "Operative removed")} disabled={saving}
                className="rounded-full p-0.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50"
                title="Remove from job" data-testid={`in-house-crew-remove-${id}`}>
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      {ids.length > 1 && <p className="text-[10px] text-muted-foreground">★ lead · {ids.length} operatives — each checks in on site separately.</p>}
      {(currentWarnings.length > 0 || lastWarnings.length > 0) && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-2 space-y-0.5" data-testid="in-house-crew-hs-warnings">
          {(currentWarnings.length ? currentWarnings : lastWarnings).slice(0, 4).map((w, i) => (
            <p key={i} className="text-[11px] text-amber-800 dark:text-amber-200 flex items-start gap-1" data-testid={`in-house-crew-hs-warning-${i}`}><Warning size={12} className="shrink-0 mt-0.5" /> <span><strong>{w.operative_name}</strong> — {w.text}</span></p>
          ))}
          <p className="text-[10px] text-amber-700/80 dark:text-amber-300/80">Competency warning only — the assignment stands. Update records in Health &amp; Safety → Competency.</p>
        </div>
      )}
      {!adding ? (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAdding(true)} disabled={saving || available.length === 0} data-testid="in-house-crew-add-btn">
            <Plus size={12} className="mr-1" /> {ids.length ? "Add operative" : "Assign operative"}
          </Button>
          {ids.length > 1 && (
            <button onClick={() => save([], "Crew unassigned")} disabled={saving} className="text-xs text-red-600 hover:text-red-700" data-testid="in-house-operative-unassign">Unassign all</button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Select onValueChange={(v) => save([...ids, v], ids.length ? "Operative added to crew" : "Operative assigned")} disabled={saving}>
            <SelectTrigger className="text-xs h-8 flex-1" data-testid="in-house-operative-select">
              <SelectValue placeholder={saving ? "Saving…" : "Select operative…"} />
            </SelectTrigger>
            <SelectContent>
              {available.map((u) => { const ws = warnFor(u._id); return (
                <SelectItem key={u._id} value={u._id} data-testid={`in-house-operative-option-${u._id}`}>
                  <span className="flex items-center gap-1.5">{u.name || u.email}{ws.length > 0 && <span className={`inline-flex items-center gap-0.5 text-[10px] ${warnTone(ws)}`} title={ws.map((w) => w.text).join(" · ")}><Warning size={11} /> {ws.length}</span>}</span>
                </SelectItem>); })}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}
